import { Eye, EyeOff } from 'lucide-react';
import type { QuoteViewer } from './types';
import { Card, cn } from './ui/primitives';

/**
 * Who has opened this quote.
 *
 * The headline counts the CLIENT only — it is what the "not viewed yet" badge
 * reads, and it is the number anyone chasing an answer actually wants. The list
 * below counts everyone, admins included. The two are expected to disagree.
 */
export function QuoteViews({
	headline,
	sub,
	summary,
	viewers,
	viewed,
	emptyLabel = 'No one has opened this quote yet.',
}: {
	headline: string;
	sub?: string | null;
	/** "6 views · 2 people", across everyone. */
	summary?: string | null;
	viewers: QuoteViewer[];
	/** Drives the icon — an unopened quote should look unopened. */
	viewed: boolean;
	emptyLabel?: string;
}) {
	return (
		<Card>
			<div className="flex flex-wrap items-center gap-2 border-b border-line-1 px-4 py-3">
				{viewed ? (
					<Eye size={14} className="shrink-0 text-text-3" />
				) : (
					<EyeOff size={14} className="shrink-0 text-text-4" />
				)}
				<span
					className={cn('text-base font-medium', viewed ? 'text-text-1' : 'text-text-4')}
				>
					{headline}
				</span>
				{sub && <span className="text-sm text-text-4">{sub}</span>}
				{summary && (
					<span className="ml-auto font-mono text-xs text-text-4">{summary}</span>
				)}
			</div>
			{viewers.length === 0 ? (
				<div className="px-4 py-3 text-sm text-text-4">{emptyLabel}</div>
			) : (
				viewers.map((viewer, i) => (
					<div
						key={viewer.id}
						className={cn(
							'flex items-center gap-3 px-4 py-3',
							i > 0 && 'border-t border-line-1'
						)}
					>
						{viewer.avatar}
						<div className="min-w-0 flex-1">
							<div className="truncate text-base font-medium text-text-1">
								{viewer.name}
								{viewer.email && viewer.email !== viewer.name && (
									<span className="font-normal text-text-3">
										{' '}
										· {viewer.email}
									</span>
								)}
							</div>
							{viewer.role && (
								<div className="mt-px truncate text-xs text-text-4">
									{viewer.role}
								</div>
							)}
						</div>
						<span className="shrink-0 text-right">
							<span className="block font-mono text-base font-medium text-text-1">
								{viewer.viewCount}
							</span>
							<span className="mt-px block text-2xs text-text-4">
								{viewer.lastViewed}
							</span>
						</span>
					</div>
				))
			)}
		</Card>
	);
}
